import { memo, useId, useMemo } from "react";

import { t } from "../../i18n/i18n";
import { FRIEND_BLUE, symbolSvg } from "../../map/units/symbols";

/** Fond commun des croquis (modes d'action, DIREX) : projection de l'emprise, cours d'eau, routes, fuseau et
 *  lignes de coordination, localités, unités amies, nord et échelle. Nord en haut, comme la carte ; la carte
 *  elle-même n'est pas modifiée. */

export const SKETCH_WIDTH = 640;
/** Fond des croquis : jeton `canvas` (R2). */
export const SKETCH_BG = "var(--color-canvas)";
// ui-check: ignore-start — fond cartographique des croquis (eau, routes) : teintes du fond de carte, sans
// équivalent parmi les jetons de l'interface (la carte est gelée, le croquis la reprend en plus simple).
const WATER = "#a9cfe9";
const ROAD_MAJOR = "#b7bcc6";
const ROAD_MINOR = "#cdd1d8";
// ui-check: ignore-end

export interface SketchGround {
  bbox: [number, number, number, number];
  corridor: {
    upper: [number, number][];
    lower: [number, number][];
    lines: { name: string; coordinates: [number, number][] }[];
  } | null;
  roads: { class: string; coordinates: [number, number][] }[];
  rivers: { class: string; coordinates: [number, number][] }[];
  localities: { name: string; lon: number; lat: number }[];
}

export interface Projection {
  x: (lon: number) => number;
  y: (lat: number) => number;
  height: number;
  metersPerUnit: number;
}

const ROAD_WIDTH: Record<string, number> = { motorway: 2.6, trunk: 2.2, primary: 1.7, secondary: 1.25, tertiary: 0.8 };

/** Projection équirectangulaire de l'emprise, proportions bornées (ni trop plat, ni trop haut). */
export function projection(bbox: SketchGround["bbox"]): Projection {
  let [x0, y0, x1, y1] = bbox;
  const k = Math.cos((((y0 + y1) / 2) * Math.PI) / 180);
  let w = (x1 - x0) * k;
  let h = y1 - y0;
  if (h / w < 0.55) {
    const extra = (0.55 * w - h) / 2;
    y0 -= extra;
    y1 += extra;
    h = y1 - y0;
  }
  if (h / w > 1.05) {
    const extra = (h / 1.05 - w) / 2 / k;
    x0 -= extra;
    x1 += extra;
    w = (x1 - x0) * k;
  }
  const scale = SKETCH_WIDTH / w;
  return {
    x: (lon) => (lon - x0) * k * scale,
    y: (lat) => (y1 - lat) * scale,
    height: h * scale,
    metersPerUnit: 111_320 / scale,
  };
}

export function pathOf(p: Pick<Projection, "x" | "y">, coords: [number, number][], close = false): string {
  const d = coords.map(([lon, lat], i) => `${i ? "L" : "M"}${p.x(lon).toFixed(1)} ${p.y(lat).toFixed(1)}`).join("");
  return close ? `${d}Z` : d;
}

/** Barre d'échelle : 1, 2, 5, 10, 20 ou 50 km, environ un cinquième de la largeur. */
function scaleBar(p: Projection): { units: number; label: string } {
  const target = (SKETCH_WIDTH * 0.2 * p.metersPerUnit) / 1000;
  const km = [50, 20, 10, 5, 2, 1, 0.5].find((v) => v <= target) ?? 0.5;
  return { units: (km * 1000) / p.metersPerUnit, label: km < 1 ? `${km * 1000} m` : `${km} km` };
}

/** Images des symboles APP-6 des unités amies (milsymbol), par code SIDC. */
export function useSymbolIcons(sidcs: string[]) {
  const key = [...new Set(sidcs)].sort().join(",");
  return useMemo(() => {
    const out = new Map<string, { href: string; w: number; h: number }>();
    for (const sidc of key ? key.split(",") : []) {
      const svg = symbolSvg(sidc, 15);
      const w = Number(/width="([\d.]+)"/.exec(svg)?.[1] ?? 28);
      const h = Number(/height="([\d.]+)"/.exec(svg)?.[1] ?? 22);
      out.set(sidc, { href: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`, w, h });
    }
    return out;
  }, [key]);
}

/** Nom de localité posé : point d'ancrage du texte (`y` : milieu de la ligne) et alignement (`start` : à droite du
 *  point, `end` : à gauche, `middle` : centré au-dessus ou au-dessous) ; `null` : nom omis (il recouvrirait un nom
 *  déjà posé de tous côtés), seul le point reste. */
export interface PlacedLocality {
  name: string;
  x: number;
  y: number;
  anchor: "start" | "end" | "middle" | null;
}

/** Boîte occupée dans le croquis (unités du `viewBox`). */
export interface SketchBox {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

/** Largeur estimée d'un nom de localité en 10 px (moyenne de la police de l'interface), par excès. */
function localityWidth(name: string): number {
  return name.length * 5.4 + 2;
}

/** Boîtes des symboles des unités du croquis (image centrée sur le point) : obstacles des noms. */
export function unitBoxes(
  p: Pick<Projection, "x" | "y">,
  units: readonly { sidc: string; lon: number; lat: number }[],
  icons: Map<string, { w: number; h: number }>,
): SketchBox[] {
  return units.flatMap((u) => {
    const icon = icons.get(u.sidc);
    if (!icon) return [];
    const x = p.x(u.lon);
    const y = p.y(u.lat);
    return [{ x0: x - icon.w / 2, y0: y - icon.h / 2, x1: x + icon.w / 2, y1: y + icon.h / 2 }];
  });
}

/** Marge intérieure du croquis où une étiquette de ligne reste lisible. */
const EDGE = 14;
/** Écart entre le bout d'une ligne et le bord de son étiquette (comme les puces de la carte). */
const LINE_LABEL_GAP = 4;

/** Étiquette d'une LIMA du croquis, comme sur la carte : au-delà du bout de la ligne situé du côté de la limite
 *  `labelRail` (celui qui porte la puce sur la carte : limite « droite » de l'iPad, `lower` du croquis,
 *  `mapdraw.label_at_first`), dans l'axe de son dernier segment. Bout hors du croquis : près du bord, à côté de la
 *  ligne, à l'intérieur. Rend le point du texte (centre, `y` : milieu de la ligne de texte) et sa boîte ; null sans
 *  deux points. Fonction pure. */
export function lineLabelSpot(
  p: Pick<Projection, "x" | "y" | "height">,
  line: { name: string; coordinates: [number, number][] },
  labelRail: readonly [number, number][],
  width = SKETCH_WIDTH,
): { x: number; y: number; box: SketchBox } | null {
  const pts = line.coordinates.map(([lon, lat]) => [p.x(lon), p.y(lat)] as [number, number]);
  if (pts.length < 2) return null;
  // Bout du côté de la limite `labelRail` : celui des deux qui en est le plus proche.
  const rail = labelRail.map(([lon, lat]) => [p.x(lon), p.y(lat)] as [number, number]);
  const dist = (q: [number, number]) => (rail.length ? Math.min(...rail.map((r) => Math.hypot(r[0] - q[0], r[1] - q[1]))) : 0);
  if (rail.length && dist(pts[0]!) < dist(pts[pts.length - 1]!)) pts.reverse();
  const inside = (q: [number, number]) => q[0] >= EDGE && q[0] <= width - EDGE && q[1] >= EDGE && q[1] <= p.height - EDGE;
  const w = line.name.length * 6.2 + 4;
  const h = 12;
  const boxAt = (cx: number, cy: number) => ({ x0: cx - w / 2, y0: cy - h / 2, x1: cx + w / 2, y1: cy + h / 2 });
  const tip = pts[pts.length - 1]!;
  const prev = pts[pts.length - 2]!;
  const d = Math.hypot(tip[0] - prev[0], tip[1] - prev[1]) || 1;
  const u = [(tip[0] - prev[0]) / d, (tip[1] - prev[1]) / d] as const;
  const m = Math.max(Math.abs(u[0]), Math.abs(u[1])) || 1;
  if (inside(tip)) {
    // Au-delà du bout, dans l'axe : bord de l'étiquette à `LINE_LABEL_GAP` du bout (boîte des puces de la carte).
    const cx = tip[0] + u[0] * LINE_LABEL_GAP + (u[0] / m) * (w / 2);
    const cy = tip[1] + u[1] * LINE_LABEL_GAP + (u[1] / m) * (h / 2);
    const box = boxAt(cx, cy);
    if (box.x0 >= 2 && box.x1 <= width - 2 && box.y0 >= 2 && box.y1 <= p.height - 2) return { x: cx, y: cy, box };
  }
  // Bout hors du croquis (ou étiquette qui en sortirait) : dernier point visible en remontant la ligne.
  for (let k = pts.length - 1; k > 0; k--) {
    const a = pts[k - 1]!;
    const b = pts[k]!;
    if (!inside(a)) continue;
    // Point de sortie sur le cadre intérieur, par dichotomie sur [a, b].
    let lo = 0;
    let hi = 1;
    for (let i = 0; i < 24; i++) {
      const mid = (lo + hi) / 2;
      if (inside([a[0] + (b[0] - a[0]) * mid, a[1] + (b[1] - a[1]) * mid])) lo = mid;
      else hi = mid;
    }
    const exit: [number, number] = [a[0] + (b[0] - a[0]) * lo, a[1] + (b[1] - a[1]) * lo];
    // À côté de la ligne, en retrait du bord : à droite de la ligne (sens écran), sinon à gauche.
    const side = [-u[1], u[0]] as const;
    const back = [exit[0] - u[0] * (h / 2 + 2), exit[1] - u[1] * (h / 2 + 2)] as const;
    for (const sign of [1, -1]) {
      const cx = back[0] + sign * side[0] * (w / 2 + 4);
      const cy = back[1] + sign * side[1] * (h / 2 + 4);
      const box = boxAt(cx, cy);
      if (box.x0 >= 2 && box.x1 <= width - 2 && box.y0 >= 2 && box.y1 <= p.height - 2) return { x: cx, y: cy, box };
    }
    return null;
  }
  return null;
}

/** Noms des localités sans chevauchement, dans l'ordre reçu (les premiers gardent leur place) : à droite du point
 *  (à gauche près du bord droit), sinon de l'autre côté, sinon centré au-dessus puis au-dessous (à 15 px, puis à
 *  24 px) ; un nom qui
 *  recouvrirait un nom déjà posé ou un obstacle (`obstacles` : étiquettes des LIMA, symboles des unités) de tous
 *  côtés, ou sortirait du croquis, est omis (le point reste). Fonction pure. */
export function placeLocalities(
  localities: SketchGround["localities"],
  p: Pick<Projection, "x" | "y">,
  width = SKETCH_WIDTH,
  obstacles: readonly SketchBox[] = [],
): PlacedLocality[] {
  const boxes: SketchBox[] = [...obstacles];
  const GAP = 2;
  const LIFT = 15; // décalage vertical d'un nom centré au-dessus ou au-dessous du point
  const FAR = 24; // second décalage, au-delà d'un symbole d'unité (22 px de haut) centré sur le point
  return localities.map((l) => {
    const x = p.x(l.lon);
    const y = p.y(l.lat);
    const w = localityWidth(l.name);
    const sides: { anchor: "start" | "end" | "middle"; tx: number; ty: number; x0: number }[] = [
      { anchor: "start", tx: x + 5, ty: y, x0: x + 5 },
      { anchor: "end", tx: x - 5, ty: y, x0: x - 5 - w },
    ];
    if (x > width - 140) sides.reverse();
    sides.push({ anchor: "middle", tx: x, ty: y - LIFT, x0: x - w / 2 }, { anchor: "middle", tx: x, ty: y + LIFT, x0: x - w / 2 });
    // Plus loin au-dessus ou au-dessous : dégage un symbole d'unité posé sur le point.
    sides.push({ anchor: "middle", tx: x, ty: y - FAR, x0: x - w / 2 }, { anchor: "middle", tx: x, ty: y + FAR, x0: x - w / 2 });
    for (const side of sides) {
      const box = { x0: side.x0, y0: side.ty - 6, x1: side.x0 + w, y1: side.ty + 6 };
      if (box.x0 < 2 || box.x1 > width - 2) continue;
      if (boxes.some((b) => box.x0 < b.x1 + GAP && b.x0 < box.x1 + GAP && box.y0 < b.y1 + GAP && b.y0 < box.y1 + GAP)) continue;
      boxes.push(box);
      return { name: l.name, x: side.tx, y: side.ty, anchor: side.anchor };
    }
    return { name: l.name, x, y, anchor: null };
  });
}

/** Tracés consécutifs de même classe réunis en un seul chemin (sous-chemins « M… L… » à la suite) : même dessin,
 *  trait pour trait et dans le même ordre, qu'un chemin par tracé, avec quelques éléments SVG au lieu de milliers
 *  (corps d'armée en terrain libre). Fonction pure. */
export function lineRuns(p: Pick<Projection, "x" | "y">, lines: readonly { class: string; coordinates: [number, number][] }[]) {
  const runs: { class: string; d: string }[] = [];
  for (const line of lines) {
    const d = pathOf(p, line.coordinates);
    const last = runs[runs.length - 1];
    if (last && last.class === line.class) last.d += d;
    else runs.push({ class: line.class, d });
  }
  return runs;
}

/** Cours d'eau puis routes, calculés une fois par fond (le temps du croquis, les contacts du wargame ou une relecture
 *  identique du serveur ne les redessinent pas). */
const GroundLines = memo(function GroundLines({ p, roads, rivers }: { p: Projection; roads: SketchGround["roads"]; rivers: SketchGround["rivers"] }) {
  const water = useMemo(() => lineRuns(p, rivers.map((r) => ({ ...r, class: "river" }))), [p, rivers]);
  const lines = useMemo(() => lineRuns(p, roads), [p, roads]);
  return (
    <>
      {water.map((r, i) => (
        <path key={`w${i}`} d={r.d} fill="none" stroke={WATER} strokeWidth={1.5} strokeLinecap="round" />
      ))}
      {lines.map((r, i) => (
        <path
          key={`r${i}`}
          d={r.d}
          fill="none"
          stroke={r.class === "motorway" || r.class === "trunk" ? ROAD_MAJOR : ROAD_MINOR}
          strokeWidth={ROAD_WIDTH[r.class] ?? 1}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ))}
    </>
  );
});

/** Cours d'eau, routes, fuseau et lignes (étiquette au-delà du bout, `lineLabelSpot`), localités
 *  (`placeLocalities`, à l'écart des étiquettes des lignes et des `obstacles` : symboles des unités). */
export function SketchGroundLayers({ p, ground, obstacles = [] }: { p: Projection; ground: SketchGround; obstacles?: readonly SketchBox[] }) {
  // Limite qui porte les étiquettes des LIMA : la « droite » de l'iPad (`lower`, `upper` étant sa « gauche »).
  const labelRail = ground.corridor ? (ground.corridor.lower.length >= 2 ? ground.corridor.lower : ground.corridor.upper) : [];
  const labels = (ground.corridor?.lines ?? []).map((line) => lineLabelSpot(p, line, labelRail));
  const taken = [...obstacles, ...labels.flatMap((label) => (label ? [label.box] : []))];
  return (
    <>
      <GroundLines p={p} roads={ground.roads} rivers={ground.rivers} />
      {ground.corridor && (
        <g stroke={FRIEND_BLUE} fill="none">
          <path d={pathOf(p, ground.corridor.upper)} strokeWidth={1.8} strokeDasharray="7 4" />
          <path d={pathOf(p, ground.corridor.lower)} strokeWidth={1.8} strokeDasharray="7 4" />
          {ground.corridor.lines.map((line, i) => {
            const label = labels[i];
            return (
              <g key={line.name}>
                <path d={pathOf(p, line.coordinates)} strokeWidth={1.1} opacity={0.8} />
                {label && (
                  <text x={label.x} y={label.y + 3.5} textAnchor="middle" fontSize={10} fontWeight={600} fill="var(--color-friend)"
                    stroke={SKETCH_BG} strokeWidth={3} paintOrder="stroke">
                    {line.name}
                  </text>
                )}
              </g>
            );
          })}
        </g>
      )}
      {placeLocalities(ground.localities, p, SKETCH_WIDTH, taken).map((l, i) => {
        const dot = ground.localities[i]!;
        return (
          <g key={l.name}>
            <circle cx={p.x(dot.lon)} cy={p.y(dot.lat)} r={2} fill="var(--color-ink-muted)" />
            {l.anchor && (
              <text x={l.x} y={l.y + 3.5} textAnchor={l.anchor} fontSize={10} fill="var(--color-ink-soft)"
                stroke={SKETCH_BG} strokeWidth={3} paintOrder="stroke">
                {l.name}
              </text>
            )}
          </g>
        );
      })}
    </>
  );
}

/** Unités amies (symboles APP-6), avec une info-bulle. */
export function FriendlyUnits({
  p,
  units,
  icons,
  suffix,
}: {
  p: Projection;
  units: { designation: string; sidc: string; lon: number; lat: number }[];
  icons: Map<string, { href: string; w: number; h: number }>;
  suffix?: string;
}) {
  return (
    <>
      {units.map((u, i) => {
        const icon = icons.get(u.sidc);
        if (!icon) return null;
        return (
          <image key={`a${i}`} href={icon.href} x={p.x(u.lon) - icon.w / 2} y={p.y(u.lat) - icon.h / 2} width={icon.w}
            height={icon.h} opacity={0.9}>
            <title>{suffix ? `${u.designation} (${suffix})` : u.designation}</title>
          </image>
        );
      })}
    </>
  );
}

/** Nord, sens de l'axe amis → ennemi (facultatif) et barre d'échelle. */
/** Mention des sources d'une zone libre sous un croquis ou au pied d'un rapport (« © contributeurs OpenStreetMap
 *  (ODbL) · Relief : … », servie par le serveur dans la langue du dossier ; docs/14 §6.10) ; rien pour un camp. */
export function SourcesNote({ text, className = "px-4 py-1.5" }: { text?: string | null; className?: string }) {
  if (!text) return null;
  return <p className={`${className} text-meta leading-snug text-ink-muted`}>{text}</p>;
}

export function SketchMarks({ p, axisAngle }: { p: Projection; axisAngle?: number }) {
  const uid = useId().replace(/:/g, "");
  const bar = scaleBar(p);
  return (
    <>
      <defs>
        <marker id={`${uid}-axis`} viewBox="0 0 8 8" refX="6.5" refY="4" markerWidth="6" markerHeight="6" orient="auto">
          <path d="M0 0.5 L8 4 L0 7.5 Z" fill="var(--color-ink-soft)" />
        </marker>
      </defs>
      <g transform={`translate(${SKETCH_WIDTH - 22} 24)`} fill="var(--color-ink-soft)">
        <path d="M0 -12 L5 3 L0 0 L-5 3 Z" />
        <text y={15} textAnchor="middle" fontSize={9.5} fontWeight={600}>
          N
        </text>
      </g>
      {axisAngle !== undefined && (
        // Flèche et légende à 96 px du bord droit : « towards EN » (anglais, plus long) garde sa marge.
        <g transform={`translate(${SKETCH_WIDTH - 96} ${p.height - 22})`}>
          <g transform={`rotate(${axisAngle})`}>
            <line x1={-14} x2={12} y1={0} y2={0} stroke="var(--color-ink-soft)" strokeWidth={1.4} markerEnd={`url(#${uid}-axis)`} />
          </g>
          <text x={20} y={3.5} fontSize={9.5} fill="var(--color-ink-soft)">
            {t("vers l'ENI")}
          </text>
        </g>
      )}
      <g transform={`translate(16 ${p.height - 16})`}>
        <rect x={-4} y={-15} width={bar.units + 8} height={21} rx={3} fill={SKETCH_BG} opacity={0.85} />
        <path d={`M0 0 H${bar.units.toFixed(1)} M0 -4 V0 M${bar.units.toFixed(1)} -4 V0`} stroke="var(--color-ink-soft)" strokeWidth={1.3} fill="none" />
        <text x={bar.units / 2} y={-6} textAnchor="middle" fontSize={9.5} fill="var(--color-ink-soft)">
          {bar.label}
        </text>
      </g>
    </>
  );
}
